import { APP_TIMEZONE } from './equipeWeekParis.js';

function formatHourLabel(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':');
  const hour = String(Number(h));
  return m && m !== '00' ? `${hour}h${m}` : `${hour}h`;
}

function formatPlageLabel(plage) {
  return `${formatHourLabel(plage.heureDebut)}–${formatHourLabel(plage.heureFin)}`;
}

function overlapsDay(absence, ymd) {
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin);
  const dayStart = new Date(`${ymd}T00:00:00`);
  const dayEnd = new Date(`${ymd}T23:59:59`);
  return start <= dayEnd && end >= dayStart;
}

function isFullDayAbsence(absence, ymd) {
  if (!overlapsDay(absence, ymd)) return false;
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_TIMEZONE,
  });
  const start = timeFmt.format(new Date(absence.dateDebut));
  const end = timeFmt.format(new Date(absence.dateFin));
  return start === '00:00' && (end === '23:59' || end === '00:00');
}

function parseHhMmToMinutes(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function getIndispoMinuteBlocksForDay(absences, ymd) {
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: APP_TIMEZONE,
  });
  const blocks = [];
  for (const a of absences || []) {
    if (!overlapsDay(a, ymd)) continue;
    if (isFullDayAbsence(a, ymd)) continue;
    const startMin = parseHhMmToMinutes(timeFmt.format(new Date(a.dateDebut)));
    const endMin = parseHhMmToMinutes(timeFmt.format(new Date(a.dateFin)));
    if (startMin == null || endMin == null || endMin <= startMin) continue;
    blocks.push({ startMin, endMin });
  }
  blocks.sort((x, y) => x.startMin - y.startMin);
  return blocks;
}

function subtractMinuteBlocksFromPlages(plages, blocks) {
  let intervals = (plages || [])
    .map((p) => ({
      startMin: parseHhMmToMinutes(p.heureDebut),
      endMin: parseHhMmToMinutes(p.heureFin),
    }))
    .filter((i) => i.startMin != null && i.endMin != null && i.endMin > i.startMin);

  for (const block of blocks) {
    const next = [];
    for (const iv of intervals) {
      if (block.endMin <= iv.startMin || block.startMin >= iv.endMin) {
        next.push(iv);
        continue;
      }
      if (block.startMin > iv.startMin) {
        next.push({ startMin: iv.startMin, endMin: Math.min(block.startMin, iv.endMin) });
      }
      if (block.endMin < iv.endMin) {
        next.push({ startMin: Math.max(block.endMin, iv.startMin), endMin: iv.endMin });
      }
    }
    intervals = next.filter((i) => i.endMin > i.startMin);
  }

  return intervals
    .sort((a, b) => a.startMin - b.startMin)
    .map((i) => ({
      heureDebut: `${String(Math.floor(i.startMin / 60)).padStart(2, '0')}:${String(i.startMin % 60).padStart(2, '0')}`,
      heureFin: `${String(Math.floor(i.endMin / 60)).padStart(2, '0')}:${String(i.endMin % 60).padStart(2, '0')}`,
    }));
}

function resolveWeeklyPlageRows(horairesConfig, jourSemaine) {
  const mode = horairesConfig?.modeHoraires || 'COMMERCE';
  const source =
    mode === 'PERSONNALISE'
      ? horairesConfig?.horaires || []
      : horairesConfig?.commerceHoraires || [];
  return source.filter((h) => h.jourSemaine === jourSemaine && h.actif !== false);
}

/**
 * Plages de base pour une date : override daté prioritaire, sinon récurrent.
 * @returns {undefined | Array} undefined = pas d'override ; [] = jour OFF explicite
 */
export function getDateExceptionPlagesForYmd(dateExceptionsByDate, ymd) {
  if (!dateExceptionsByDate || ymd == null) return undefined;
  if (!Object.prototype.hasOwnProperty.call(dateExceptionsByDate, ymd)) return undefined;
  return dateExceptionsByDate[ymd] || [];
}

function cellFromPlages(plages, { datedOverride = false } = {}) {
  if (!plages.length) {
    return { type: 'off' };
  }
  const labels = plages.map(formatPlageLabel);
  const tone = datedOverride ? 'exception' : 'full';
  if (labels.length === 1) {
    return { type: datedOverride ? 'exception' : 'full', label: labels[0] };
  }
  return { type: datedOverride ? 'exception' : 'split', lines: labels, label: tone };
}

/**
 * Construit la cellule planning collectif pour un jour.
 * @param {object} horairesConfig - réponse getInstructeurHorairesConfig
 * @param {Array} absences - indisponibilités actives
 * @param {number} jourSemaine - 0..6
 * @param {string} ymd - YYYY-MM-DD Paris
 * @param {object|null} dateExceptionsByDate - map ymd → plages override
 */
export function buildPlanningDayCell(
  horairesConfig,
  absences,
  jourSemaine,
  ymd,
  dateExceptionsByDate = null,
) {
  const dayAbsences = (absences || []).filter((a) => overlapsDay(a, ymd));
  if (dayAbsences.some((a) => isFullDayAbsence(a, ymd))) {
    return { type: 'absence', label: 'Absent' };
  }

  const exceptionPlages = getDateExceptionPlagesForYmd(dateExceptionsByDate, ymd);
  const datedOverride = exceptionPlages !== undefined;
  const basePlages =
    exceptionPlages !== undefined ? exceptionPlages : resolveWeeklyPlageRows(horairesConfig, jourSemaine);

  if (basePlages.length === 0) {
    return { type: 'off' };
  }

  const blocks = getIndispoMinuteBlocksForDay(dayAbsences, ymd);
  const effectivePlages = blocks.length
    ? subtractMinuteBlocksFromPlages(basePlages, blocks)
    : basePlages;

  if (effectivePlages.length === 0) {
    return { type: 'partial', label: 'Partiel' };
  }

  const hasPartialCut =
    blocks.length > 0 &&
    effectivePlages.length !== basePlages.length &&
    !datedOverride;
  if (hasPartialCut && !datedOverride) {
    return { type: 'partial', label: 'Partiel' };
  }

  return cellFromPlages(effectivePlages, { datedOverride });
}

export function getPresenceLabelForDay(horairesConfig, jourSemaine) {
  const mode = horairesConfig?.modeHoraires || 'COMMERCE';
  const source =
    mode === 'PERSONNALISE'
      ? horairesConfig?.horaires || []
      : horairesConfig?.commerceHoraires || [];
  const plages = source.filter((h) => h.jourSemaine === jourSemaine && h.actif !== false);
  if (!plages.length) return null;
  return plages.map(formatPlageLabel).join(' · ');
}

export function formatAbsenceBlock(dateDebut, dateFin, motif) {
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_TIMEZONE,
  });
  const start = timeFmt.format(new Date(dateDebut));
  const end = timeFmt.format(new Date(dateFin));
  const sameDay =
    new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(new Date(dateDebut)) ===
    new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(new Date(dateFin));
  if (sameDay) {
    const fullDay = start === '00:00' && (end === '23:59' || end === '00:00');
    if (fullDay) return { title: motif || 'Absente', detail: 'Toute la journée' };
    return {
      title: motif || 'Indisponible',
      detail: `${start.replace(':', 'h')} → ${end.replace(':', 'h')}`,
    };
  }
  return { title: motif || 'Congés', detail: 'Plusieurs jours' };
}

/**
 * Jour commerce fermé = aucune plage active ce jour (schéma Horaire, pas de booléen "ferme").
 * Ne change pas la règle métier PERSONNALISE — aide UX Admin uniquement.
 */
export function isCommerceClosedOnDay(commerceHoraires, jourSemaine) {
  const jour = Number(jourSemaine);
  const active = (commerceHoraires || []).filter((h) => {
    const j = h.jourSemaine != null ? Number(h.jourSemaine) : Number(h.jour);
    return j === jour && h.actif !== false;
  });
  return active.length === 0;
}

/**
 * Normalise modeHoraires API/DB pour les radios Planning (COMMERCE | PERSONNALISE).
 * Préserve la valeur connue si la réponse est vide/invalide (évite pastille « vide »).
 */
export function normalizeModeHoraires(raw, fallback = 'COMMERCE') {
  const mode = String(raw || '')
    .trim()
    .toUpperCase();
  if (mode === 'COMMERCE' || mode === 'PERSONNALISE') return mode;
  const fb = String(fallback || '')
    .trim()
    .toUpperCase();
  if (fb === 'COMMERCE' || fb === 'PERSONNALISE') return fb;
  return 'COMMERCE';
}

/**
 * État radio après load / PUT — la pastille doit toujours matcher la valeur connue.
 * @returns {{ modeHoraires: 'COMMERCE'|'PERSONNALISE', commerceChecked: boolean, personnaliseChecked: boolean }}
 */
export function resolveModeHorairesRadioState(apiMode, previousMode = 'COMMERCE') {
  const modeHoraires = normalizeModeHoraires(apiMode, previousMode);
  return {
    modeHoraires,
    commerceChecked: modeHoraires === 'COMMERCE',
    personnaliseChecked: modeHoraires === 'PERSONNALISE',
  };
}
